<script setup>
import { computed, ref } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import PaymentMethods from '@/Components/Public/PaymentMethods.vue'
import { platformLabel } from '@/lib/platformLabel'
import TurnstileWidget from '@/Components/Public/TurnstileWidget.vue'
import DateField from '@/Components/Public/DateField.vue'

// "Plan my day" — the concierge enquiry behind the private-charter home.
// Posts a BookingRequest with no listing (occasion + format instead); the
// controller mirrors it to the CRM queue, the inbox and the operator mail.
defineOptions({ layout: PublicLayout })

const props = defineProps({
  prefix:       { type: String, default: '' },
  prefill:      { type: Object, default: () => ({}) },
  occasions:    { type: Array,  default: () => [] },
  trip_formats: { type: Array,  default: () => [] },
})

const { t } = useI18n()
const page = usePage()
const writeUs = computed(() => page.props.brand?.channels?.message || [])

const today = new Date().toISOString().slice(0, 10)

const form = useForm({
  occasion:    props.prefill?.occasion    || '',
  trip_format: props.prefill?.trip_format || '',
  start_on:    props.prefill?.start_on    || '',
  end_on:      props.prefill?.end_on      || '',
  adults:      props.prefill?.adults      || 2,
  children:    0,
  name: '', phone: '', email: '', note: '',
  website: '', turnstile_token: '',
})

const multiDay = computed(() => form.trip_format === 'multi_day' || form.occasion === 'multi_day')
const turnstile = ref(null)

function submit() {
  form.transform(d => ({
    booking_request: {
      occasion: d.occasion, trip_format: d.trip_format,
      start_on: d.start_on, end_on: multiDay.value ? d.end_on : '',
      adults: d.adults, children: d.children,
      name: d.name, phone: d.phone, email: d.email, note: d.note,
    },
    website: d.website,
    turnstile_token: d.turnstile_token,
  })).post(`${props.prefix}/plan`, {
    preserveScroll: true,
    onFinish: () => turnstile.value?.reset(),
  })
}

</script>

<template>
  <Head>
    <title>{{ t('public.plan.head_title') }}</title>
    <meta name="description" :content="t('public.plan.lede')">
  </Head>

  <section class="plan">
    <div class="plan__inner">
      <header class="plan__head">
        <p class="eyebrow">{{ t('public.nav.concierge') }}</p>
        <h1 class="plan__title">{{ t('public.plan.title') }}</h1>
        <p class="plan__lede">{{ t('public.plan.lede') }}</p>
      </header>

      <div class="plan__grid">
        <form class="pform" @submit.prevent="submit">
          <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" class="pform__honeypot" aria-hidden="true">

          <div class="pform__row">
            <label class="pform__field">
              <span class="pform__label">{{ t('public.plan.occasion') }}</span>
              <select v-model="form.occasion" class="pform__input" required>
                <option value="" disabled>{{ t('public.charter_home.planner.pick') }}</option>
                <option v-for="o in occasions" :key="o" :value="o">{{ t(`public.plan.occasions.${o}`) }}</option>
              </select>
            </label>
            <label class="pform__field">
              <span class="pform__label">{{ t('public.plan.format') }}</span>
              <select v-model="form.trip_format" class="pform__input">
                <option value="">{{ t('public.charter_home.planner.pick') }}</option>
                <option v-for="f in trip_formats" :key="f" :value="f">{{ t(`public.plan.formats.${f}`) }}</option>
              </select>
            </label>
          </div>

          <div class="pform__row">
            <label class="pform__field">
              <span class="pform__label">{{ t('public.plan.date') }}</span>
              <DateField v-model="form.start_on" class="pform__input" :min="today" required :placeholder="t('public.pick_date')" />
            </label>
            <label v-if="multiDay" class="pform__field">
              <span class="pform__label">{{ t('public.plan.end_date') }}</span>
              <DateField v-model="form.end_on" class="pform__input" :min="form.start_on || today" :placeholder="t('public.pick_date')" />
            </label>
            <label class="pform__field pform__field--narrow">
              <span class="pform__label">{{ t('public.plan.guests') }}</span>
              <input v-model.number="form.adults" type="number" class="pform__input" min="1" max="100" required>
            </label>
            <label class="pform__field pform__field--narrow">
              <span class="pform__label">{{ t('public.plan.children') }}</span>
              <input v-model.number="form.children" type="number" class="pform__input" min="0" max="100">
            </label>
          </div>

          <label class="pform__field">
            <span class="pform__label">{{ t('public.plan.name') }}</span>
            <input v-model="form.name" type="text" class="pform__input" required maxlength="80" autocomplete="name">
          </label>
          <div class="pform__row">
            <label class="pform__field">
              <span class="pform__label">{{ t('public.plan.phone') }}</span>
              <input v-model="form.phone" type="tel" class="pform__input" autocomplete="tel">
            </label>
            <label class="pform__field">
              <span class="pform__label">{{ t('public.plan.email') }}</span>
              <input v-model="form.email" type="email" class="pform__input" autocomplete="email">
            </label>
          </div>
          <p class="pform__hint">{{ t('public.plan.contact_hint') }}</p>

          <label class="pform__field">
            <span class="pform__label">{{ t('public.plan.note') }}</span>
            <textarea v-model="form.note" class="pform__input pform__textarea" rows="4" maxlength="2000" />
          </label>

          <TurnstileWidget ref="turnstile" v-model="form.turnstile_token" />

          <button type="submit" class="pform__submit" :disabled="form.processing">
            {{ t('public.plan.submit') }}
          </button>
          <PaymentMethods inline />

          <div v-if="writeUs.length" class="pform__chat">
            <span>{{ t('public.plan.or_chat') }}</span>
            <a v-for="c in writeUs" :key="c.id" :href="c.url" target="_blank" rel="noopener" class="pform__chan">{{ platformLabel(c.platform) }}</a>
          </div>
        </form>

        <aside class="aside">
          <h2 class="aside__title">{{ t('public.plan.aside.title') }}</h2>
          <ol class="aside__steps">
            <li><span class="aside__n">01</span><p>{{ t('public.plan.aside.one') }}</p></li>
            <li><span class="aside__n">02</span><p>{{ t('public.plan.aside.two') }}</p></li>
            <li><span class="aside__n">03</span><p>{{ t('public.plan.aside.three') }}</p></li>
          </ol>
          <ul class="aside__trust">
            <li>{{ t('public.charter_home.trust.licensed') }}</li>
            <li>{{ t('public.charter_home.trust.chat') }}</li>
            <li>{{ t('public.charter_home.trust.pay') }}</li>
          </ul>
        </aside>
      </div>
    </div>
  </section>
</template>

<style scoped>
.plan { width: 100%; background: linear-gradient(180deg, var(--bg) 0%, var(--sand, var(--surfaceAlt)) 60%, var(--bg) 100%); }
.plan__inner { max-width: 72rem; margin: 0 auto; padding: clamp(2rem, 5vw, 4rem) clamp(1.25rem, 4vw, 3rem) clamp(3rem, 6vw, 5rem); }
.plan__head { max-width: 46rem; margin-bottom: clamp(1.75rem, 4vw, 2.75rem); }
.eyebrow { margin: 0; font-size: 0.75rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 700; color: var(--accent); }
.plan__title { font-family: var(--font-display, var(--display)); font-weight: 500; letter-spacing: -0.005em; font-size: clamp(2.4rem, 5vw, 3.75rem); line-height: 1.02; margin: 0.6rem 0 0; color: var(--ink); text-wrap: balance; }
.plan__lede { margin: 1rem 0 0; font-size: 1.1rem; line-height: 1.55; color: var(--inkSoft); max-width: 52ch; }

.plan__grid { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(16rem, 1fr); gap: 2.5rem; align-items: start; }

.pform { background: var(--surface); border: 1px solid var(--line); border-radius: 1.5rem; padding: clamp(1.25rem, 3vw, 2rem); display: grid; gap: 1rem; box-shadow: 0 30px 60px -40px color-mix(in oklch, var(--ink) 40%, transparent); }
.pform__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.pform__row { display: grid; grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr)); gap: 1rem; }
.pform__field { display: grid; gap: 0.35rem; min-width: 0; }
.pform__field--narrow { max-width: 9rem; }
.pform__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: var(--muted); }
.pform__input { width: 100%; box-sizing: border-box; padding: 0.8rem 0.95rem; border: 1px solid var(--line); border-radius: 0.85rem; background: var(--bg); color: var(--ink); font: inherit; font-weight: 500; }
.pform__input:focus { outline: 2px solid var(--second); outline-offset: 2px; border-color: var(--second); }
.pform__textarea { resize: vertical; }
.pform__hint { margin: -0.4rem 0 0; font-size: 0.85rem; color: var(--muted); }
/* Mirrors LuxButton (light tone, solid) — stays a real <button> for the form. */
.pform__submit { margin-top: 0.25rem; display: inline-flex; align-items: center; justify-content: center; padding: 0.95rem 1.6rem; border-radius: 999px; cursor: pointer; appearance: none; font-family: var(--font-body, var(--body)); font-size: 0.78rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; background: var(--night, var(--ink)); color: var(--foam, #ECF3F6); border: 1px solid var(--night, var(--ink)); box-shadow: 0 16px 34px -20px color-mix(in oklch, var(--ink) 60%, transparent), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 40%, transparent); transition: transform 160ms ease, background 160ms ease; }
.pform__submit:hover:not(:disabled) { transform: translateY(-1px); background: var(--night2, var(--ink)); border-color: var(--night2, var(--ink)); }
.pform__submit:disabled { opacity: 0.6; cursor: default; transform: none; }
.pform__chat { display: flex; flex-wrap: wrap; gap: 0.5rem 0.9rem; align-items: center; font-size: 0.9rem; color: var(--inkSoft); }
.pform__chan { color: var(--azure, var(--second)); font-weight: 700; text-decoration: none; }
.pform__chan:hover { text-decoration: underline; }

.aside { display: grid; gap: 1.5rem; padding-top: 0.5rem; }
.aside__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.9rem; line-height: 1.05; margin: 0; color: var(--ink); }
.aside__steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 1rem; }
.aside__steps li { display: grid; grid-template-columns: 2.5rem 1fr; gap: 0.75rem; align-items: start; border-top: 1px solid var(--line); padding-top: 0.9rem; }
.aside__steps p { margin: 0; color: var(--inkSoft); line-height: 1.5; }
.aside__n { font-family: var(--font-mono, var(--mono)); font-size: 0.8rem; color: var(--accent); padding-top: 0.2rem; }
.aside__trust { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.5rem; font-size: 0.9rem; color: var(--inkSoft); }
.aside__trust li { display: flex; gap: 0.6rem; align-items: center; }
.aside__trust li::before { content: ''; width: 0.45rem; height: 0.45rem; border-radius: 50%; background: var(--second); flex: none; }

@media (max-width: 56rem) {
  .plan__grid { grid-template-columns: 1fr; }
  .pform__field--narrow { max-width: none; }
}
</style>
